import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../Hooks/useApi';
import { useItemSubCategoryApi } from '../../Hooks/useItemSubCategory';
import { CreateItemCategoryRequest } from '../../client-sdk';
import { useSnackbar } from '../stateless/SnackBarProvider';

export interface ItemCategory {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  status: 'blocked' | 'unblocked' | string;
  addedBy: string;
  categoryImage?: string;
  categoryIcon?: string;
  subCategories?: ItemCategory[];
}

export const useItemAddCategory = () => {
  const { addItemCategory } = useApi();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationFn: ({ data }: { data: CreateItemCategoryRequest }) =>
      addItemCategory(data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['itemCategories'] });
      showMessage(response?.message || 'Store created successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to create Store',
        'error'
      );
    },
  });
};

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  addedByFilter?: string;
  statusFilter?: string;
  search?: string;
  merchantId?: string;
  storeId?: string;
};

export const useGetItemCategories = (queryParams: Params) => {

  const { getListItemCategories } = useApi();
 
  const { data, isLoading:isFetching, isRefetching, refetch, error } = useQuery({
    queryKey: [
      'itemCategories',
      queryParams
    ],
    queryFn: () => getListItemCategories(queryParams),
    retry: false,
    enabled: Boolean(
      (queryParams.merchantId && queryParams.merchantId.trim() !== '') ||
      (queryParams.storeId && queryParams.storeId.trim() !== '')
    ),
  });
  return {
    data,
    isLoading:isFetching || isRefetching,
    refetch,
    error,
  };
};

export const useEditItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editItemCategory } = useApi();

  return useMutation({
    mutationKey: ['editItemSubCategory'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editItemCategory(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item SubCategory Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the Item SubCategory',
        'error'
      );
    },
  });
};

export const useDeleteItemSubCategory = () => {
  const queryClient = useQueryClient();
  const { deleteItemSubCategory } = useItemSubCategoryApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['deleteItemSubCategory'],
    mutationFn: (ids: string[]) => {
      return deleteItemSubCategory(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item SubCategory deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemSubCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Item SubCategory',
        'error'
      );
    },
  });
};

export const useBlockUnblockItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockUnblockItemCategoryById } = useApi();

  return useMutation({
    mutationKey: ['blockUnblockItemCategory'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockUnblockItemCategoryById({ id, status: data.status }),

    onSuccess: (_response, _variables) => {
      const status = _variables.data.status;
      const action = status === 'blocked' ? 'blocked' : 'unblocked';
      showMessage(`ItemCategory ${action} successfully`, 'success');

      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },

    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update ItemCategory status',
        'error'
      );
    },
  });
};

export const useDeleteItemCategory = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { deleteItemCategoryById } = useApi();

  return useMutation({
    mutationKey: ['deleteItemCategory'],
    mutationFn: (ids: string[]) => {
      return deleteItemCategoryById(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Item Category deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['itemCategories'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Item Category',
        'error'
      );
    },
  });
};
